// Tipos compartilhados entre sim-onboarding steps
// v2 — alinhados com o banco real (municipios, estados, escolas)

export interface Cidade {
  id: string;          // municipios.id (UUID real do banco)
  nome: string;
  uf: string;          // estados.sigla via JOIN
  estadoId: string;
  lat: number | null;
  lng: number | null;
  codigoIbge?: string | null;
  temGestor: boolean;  // usuario com perfil=MUNICIPAL vinculado?
  totalEscolas: number;
}

export interface Escola {
  id: string;
  nome: string;
  alunos: number;      // totalAlunos do banco
  rede: string;        // tipoRede do banco
  isDemo?: boolean;    // is_demo=true → tem seed ativo
  isNova?: boolean;    // criada nesta sessão
  municipioId?: string | null;
  lat?: number | null; // coordenada GPS (do catálogo INEP se disponível)
  lng?: number | null;
}

export interface Estado {
  id: string;
  nome: string;
  sigla: string;
  redeId: string;
}

// Sessão de simulação — espelho do backend SimulationSession
export interface SimulationSession {
  id: string;
  municipioId: string;
  municipioNome: string;
  municipioUf: string;
  status: 'PREPARING' | 'RUNNING' | 'PAUSED' | 'STOPPED' | 'ERROR';
  escolaIds: string[];
  actorIds: string[];
  iniciadoEm: string | null;
  pausadoEm: string | null;
  encerradoEm: string | null;
  criadoEm: string;
  meta: Record<string, unknown>;
}

// Utilitários de cálculo (baseados em totalAlunos)
export function calc(n: number) {
  const turmas = Math.round(n / 30);
  return {
    alunos: n,
    turmas,
    profs:    Math.round(turmas * 2.5),
    resps:    Math.round(n * 1.8),
    equipe:   Math.max(3, Math.round(n * 0.02)),
    tags:     n,
    leitores: Math.round(turmas / 3) + 2,
  };
}

export function calcTotal(escolas: Escola[]) {
  return escolas.reduce((acc, e) => {
    const r = calc(e.alunos);
    return {
      alunos:   acc.alunos   + r.alunos,
      turmas:   acc.turmas   + r.turmas,
      profs:    acc.profs    + r.profs,
      resps:    acc.resps    + r.resps,
      equipe:   acc.equipe   + r.equipe,
      tags:     acc.tags     + r.tags,
      leitores: acc.leitores + r.leitores,
    };
  }, { alunos:0, turmas:0, profs:0, resps:0, equipe:0, tags:0, leitores:0 });
}

// Geocode via Nominatim (gratuito — fallback quando cidade não está no banco)
export async function geocodeCity(nome: string, uf: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const q = encodeURIComponent(`${nome}, ${uf}, Brasil`);
    const r = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&countrycodes=br`,
      { headers: { 'Accept-Language': 'pt-BR' } }
    );
    const data = await r.json();
    if (data.length > 0) return { lat: +data[0].lat, lng: +data[0].lon };
  } catch {}
  return null;
}

// Nome randômico para escola demo nova
const NOMES_RAND = [
  'João Paulo II','Santos Dumont','Tiradentes','Machado de Assis','Cora Coralina',
  'Rui Barbosa','Dom Pedro II','Padre Anchieta','Carlos Drummond','Cecília Meireles',
  'Castro Alves','Zumbi dos Palmares','Monteiro Lobato','Florestan Fernandes',
];
let _ni = Math.floor(Math.random() * NOMES_RAND.length);
export function nomeRand() {
  const p = ['E.M.','EMEF','EMEI','CEMA','CEI'][Math.floor(Math.random() * 5)];
  return `${p} ${NOMES_RAND[_ni++ % NOMES_RAND.length]}`;
}

// SessionStorage helper
export const SS = {
  get: (k: string) => { try { return sessionStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { sessionStorage.setItem(k, v); } catch {} },
  del: (k: string) => { try { sessionStorage.removeItem(k); } catch {} },
};

/** Flags do /simulador — localStorage + sessionStorage (sobrevive refresh) */
export function isSimUnlocked(): boolean {
  return SS.get('sim_auth') === '1' || localStorage.getItem('ac_sim_auth') === '1';
}

export function markSimUnlocked(): void {
  SS.set('sim_auth', '1');
  try { localStorage.setItem('ac_sim_auth', '1'); } catch { /* noop */ }
}

export function isSimCockpitActive(): boolean {
  return (
    SS.get('sim_cockpit') === '1' ||
    localStorage.getItem('ac_sim_cockpit') === '1' ||
    !!SS.get('sim_session_id') ||
    !!localStorage.getItem('ac_sim_session_id')
  );
}

export function markSimCockpit(sessionId?: string): void {
  SS.set('sim_cockpit', '1');
  try { localStorage.setItem('ac_sim_cockpit', '1'); } catch { /* noop */ }
  if (sessionId) {
    SS.set('sim_session_id', sessionId);
    try { localStorage.setItem('ac_sim_session_id', sessionId); } catch { /* noop */ }
  }
}

export function clearSimFlags(): void {
  SS.del('sim_auth');
  SS.del('sim_cidade');
  SS.del('sim_session_id');
  SS.del('sim_cockpit');
  try {
    localStorage.removeItem('ac_sim_auth');
    localStorage.removeItem('ac_sim_cockpit');
    localStorage.removeItem('ac_sim_session_id');
  } catch { /* noop */ }
}

export interface Rede {
  id: string;
  nome: string;
  padrao: boolean;
  totalEstados: number;
  totalMunicipios: number;
  totalEscolas: number;
  peso: number;
}

// ─── API — usa o cliente Axios (api) que já injeta Bearer automaticamente ─────

import { api } from '../../api/client';

/** Busca municípios no banco via GET /municipios/buscar?q=&redeId= */
export async function buscarMunicipios(q: string, redeId?: string): Promise<Cidade[]> {
  if (q.trim().length < 2) return [];
  try {
    const params = new URLSearchParams({ q: q.trim() });
    if (redeId) params.set('redeId', redeId);
    const { data } = await api.get(`/municipios/buscar?${params}`);
    return data;
  } catch { return []; }
}

/** Lista todas as redes escolares via GET /municipios/redes */
export async function listarRedes(): Promise<Rede[]> {
  try { const { data } = await api.get('/municipios/redes'); return data; }
  catch { return []; }
}

/** Lista todos os estados via GET /municipios/estados */
export async function listarEstados(): Promise<Estado[]> {
  try { const { data } = await api.get('/municipios/estados'); return data; }
  catch { return []; }
}

/** Cria um município via POST /municipios */
export async function criarMunicipio(dto: {
  nome: string; estadoId?: string; lat: number; lng: number; codigoIbge?: string; estadoSigla?: string; redeId?: string;
}): Promise<Cidade> {
  const { data } = await api.post('/municipios', dto);
  return data;
}

/** Cria gestor municipal via POST /municipios/:id/gestor */
export async function criarGestorMunicipal(municipioId: string, dto: {
  nome: string; email?: string; senha?: string;
}): Promise<{ gestorId: string; email: string; criado: boolean; senhaPadrao: string }> {
  const { data } = await api.post(`/municipios/${municipioId}/gestor`, dto);
  return data;
}

/** Busca escolas do município via GET /municipios/:id/escolas */
export async function buscarEscolasMunicipio(municipioId: string): Promise<Escola[]> {
  try {
    const { data } = await api.get(`/municipios/${municipioId}/escolas`);
    return data.map((e: any) => ({ 
      id: e.id, 
      nome: e.nome, 
      alunos: e.totalAlunos, 
      rede: e.tipoRede, 
      isDemo: e.isDemo, 
      municipioId: e.municipioId,
      lat: e.latitude ?? null,
      lng: e.longitude ?? null
    }));
  } catch { return []; }
}

/** Cria sessão de simulação via POST /simulation/sessions */
export async function criarSessaoSimulacao(dto: {
  municipioId: string; escolaIds: string[]; criadoPorId?: string;
}): Promise<SimulationSession> {
  const { data } = await api.post('/simulation/sessions', dto);
  return data;
}

/** Inicia sessão via POST /simulation/sessions/:id/start */
export async function iniciarSessao(sessionId: string): Promise<SimulationSession> {
  const { data } = await api.post(`/simulation/sessions/${sessionId}/start`);
  return data;
}

// ─── Catálogo de Escolas INEP ─────────────────────────────────────────────────

/** Escola do catálogo INEP enriquecida para o wizard de onboarding */
export interface CatalogoEscolaOnboarding {
  codigoInep: string;
  nome: string;
  nomeDemo: string;
  uf: string;
  municipio: string;
  porteEscola: string | null;
  etapasEnsino: string | null;
  dependenciaAdministrativa: string;
  latitude: number | null;
  longitude: number | null;
  endereco: string | null;
  telefone: string | null;
  localizacao: string | null;
  semCoordenadas: boolean;
  nAlunos: number;
}

/** GET /catalogo-escolas/onboarding?municipio=X&uf=Y&limit=N&lat=L&lng=G */
export async function buscarEscolasOnboarding(
  municipio: string,
  uf: string,
  limit = 5,
  lat?: number | null,
  lng?: number | null,
): Promise<CatalogoEscolaOnboarding[]> {
  try {
    const params = new URLSearchParams({ municipio, uf, limit: String(limit) });
    if (lat != null && !isNaN(lat)) params.set('lat', String(lat));
    if (lng != null && !isNaN(lng)) params.set('lng', String(lng));
    const { data } = await api.get(`/catalogo-escolas/onboarding?${params}`);
    return data;
  } catch { return []; }
}


/** POST /catalogo-escolas/ensure-populated */
export async function ensureCatalogoPopulado(): Promise<{ jaPopulado: boolean; importados?: number; erro?: string }> {
  try {
    const { data } = await api.post('/catalogo-escolas/ensure-populated');
    return data;
  } catch (e: any) { return { jaPopulado: false, erro: e.message }; }
}

/** POST /simulation/schools/create — cria escola demo com dados INEP */
export async function criarEscolaDemo(dto: {
  nome: string; municipioId: string; cidade: string; uf: string; nAlunos: number;
  diasHistorico?: number;
}): Promise<{ escola: { id: string; nome: string; totalAlunos?: number }; stats: Record<string, number> }> {
  const { data } = await api.post('/simulation/schools/create', {
    ...dto,
    diasHistorico: dto.diasHistorico ?? 15,
    turnos: ['MANHA', 'TARDE'],
    etapas: ['FUND_1', 'FUND_2'],
  });
  return data;
}
